import { customElement, property } from 'lit/decorators.js';
import { style } from './textarea.styles';
import { LitElement, html } from 'lit';
import { when } from 'lit/directives/when.js';
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {SbkLabelMixin} from "@/components/label/label-mixin";
import {labelStyles} from "@/components/label/label.styles";
import {RequiredValidator, Validator} from "@/components/validators/validators";
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import {unsafeHTML} from "lit/directives/unsafe-html.js";


/**
 * An example element.
 *
 * @slot - This element has a slot
 */
@customElement('sbk-textarea')
export class SbkTextarea extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    static formAssociated = true;

    static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

    _internals: ElementInternals;

    @property({ type: Boolean})
    disabled = false;

    @property()
    description: string = ''

    @property({reflect: true})
    name: string = ''

    @property()
    rows: number|null = 5;

    @property({type: Boolean})
    readonly: boolean = false

    @property({type: Boolean})
    required: boolean = false

    @property()
    value = '';

    @property()
    placeholder = '';

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': RequiredValidator,
    }

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
    }

    connectedCallback() {
        super.connectedCallback();
        this._internals.setFormValue(this.value);
    }

    static get styles() {
        return [
            labelStyles,
            style
        ];
    }

    render() {
        return html`
            <div class="input__wrapper${this.invalid ? ' input__wrapper--error' : ''}${this.disabled ? ' input__wrapper--disabled' : ''}">
                ${this.labelTemplate(`${this.name}-textarea`, false, this.invalid)}
                ${when(this.description,
                        () => html`
                                <sbk-annotation id="${this.name}-annotation" variant="info" ?disabled=${this.disabled || this.readonly}>${unsafeHTML(this.description)}</sbk-annotation>
                            `)}
                <textarea id="${this.name}-textarea"
                          class="textarea"
                          .value=${this.value}
                          .placeholder=${this.placeholder}
                          name=${this.name}
                          rows=${this.rows}
                          aria-describedby="${this.name}-annotation"
                          ?disabled=${this.disabled}
                          ?readonly=${this.readonly}
                          ?required=${this.required}
                          @blur=${this._validate}
                          @change=${this._changeValue}></textarea>
                ${this.invalid ? this.validationErrorTemplate(`${this.name}-annotation`) : ""}
            </div>
        `;
    }

    handleInput(event: InputEvent) {
        const target = event.target as HTMLTextAreaElement;
        this.value = target.value;
        this._internals.setFormValue(this.value);
    }

    _changeValue(event: Event) {
        const element = <HTMLTextAreaElement>event.target;
        this._internals.setFormValue(element.value);
        this.value = element.value;
        const textareaChangeEvent = new CustomEvent('textarea-change', {
            detail: { value: element.value },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(textareaChangeEvent);
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this.value = '';
        this._internals.setFormValue('');
    }

    setValue(): void {
        this._internals.setFormValue(this.value);
    }

    _validate() {
        const validators = this.getValidators();
        this.handleErrors(validators, this.value);
        const inputBlurEvent = new CustomEvent('textarea-blur');
        this.dispatchEvent(inputBlurEvent);
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'sbk-textarea': SbkTextarea;
    }

}

